// Constants
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '../Constants/Constants';

/**
 * Token Manager Utility
 * 
 * This utility provides consistent token management across the application
 * to prevent cross-tab token sharing issues.
 * 
 * Access tokens are stored in sessionStorage (tab-specific)
 * Refresh tokens are stored in sessionStorage (tab-specific)
 */

export const TokenManager = {
    /**
     * Get access token from sessionStorage (tab-specific)
     */
    getAccessToken: (): string | null => {
        return sessionStorage.getItem(ACCESS_TOKEN_KEY);
    },

    /**
     * Set access token in sessionStorage (tab-specific)
     */
    setAccessToken: (token: string): void => {
        sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
    },

    /**
     * Remove access token from sessionStorage
     */
    removeAccessToken: (): void => {
        sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    },

    /**
     * Get refresh token from sessionStorage (tab-specific)
     */
    getRefreshToken: (): string | null => {
        return sessionStorage.getItem(REFRESH_TOKEN_KEY);
    },

    /**
     * Set refresh token in sessionStorage (tab-specific)
     */
    setRefreshToken: (token: string, expiresInDays: number = 29 / 1440): void => {
        sessionStorage.setItem(REFRESH_TOKEN_KEY, token);
    },

    /**
     * Remove refresh token from sessionStorage
     */
    removeRefreshToken: (): void => {
        sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    },

    /**
     * Check if user has valid access token
     */
    hasValidAccessToken: (): boolean => {
        return Boolean(sessionStorage.getItem(ACCESS_TOKEN_KEY));
    },

    /**
     * Check if user has valid refresh token
     */
    hasValidRefreshToken: (): boolean => {
        return Boolean(sessionStorage.getItem(REFRESH_TOKEN_KEY));
    },

    /**
     * Clear all tokens (for logout)
     */
    clearAllTokens: (): void => {
        sessionStorage.removeItem(ACCESS_TOKEN_KEY);
        sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    },

    /**
     * Get authorization header value for API requests
     */
    getAuthorizationHeader: (): string => {
        const accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
        return accessToken ? `Bearer ${accessToken}` : '';
    }
};

export default TokenManager; 